import * as THREE from 'three';
import { UI_CONFIG } from '../config.js';

const MARKER_GEOMETRY = new THREE.CircleGeometry(0.105, 24);
const MARKER_RING_GEOMETRY = new THREE.RingGeometry(0.135, 0.17, 32);
const HIT_GEOMETRY = new THREE.CircleGeometry(0.31, 20);
const LABEL_GEOMETRY = new THREE.PlaneGeometry(0.98, 0.28);

const MARKER_MATERIAL = new THREE.MeshBasicMaterial({
  color: UI_CONFIG.hotspotColor ?? 0xf3c96b,
  depthTest: true,
  depthWrite: false,
  toneMapped: false,
});

const MARKER_HOVER_MATERIAL = new THREE.MeshBasicMaterial({
  color: UI_CONFIG.hotspotHoverColor ?? 0xffffff,
  depthTest: true,
  depthWrite: false,
  toneMapped: false,
});

const RING_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x173b43,
  transparent: true,
  opacity: 0.92,
  side: THREE.DoubleSide,
  depthWrite: false,
  toneMapped: false,
});

const RING_HOVER_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xf3c96b,
  transparent: true,
  opacity: 1,
  side: THREE.DoubleSide,
  depthWrite: false,
  toneMapped: false,
});

const HIT_MATERIAL = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  colorWrite: false,
  depthWrite: false,
  side: THREE.DoubleSide,
});

function createLabelTexture(title) {
  const canvas = document.createElement('canvas');
  canvas.width = 768;
  canvas.height = 220;

  const context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = 'rgba(10, 32, 37, 0.92)';
  context.beginPath();
  context.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 42);
  context.fill();

  context.strokeStyle = 'rgba(243, 201, 107, 0.9)';
  context.lineWidth = 5;
  context.stroke();

  context.fillStyle = '#fffaf0';
  context.font = '600 48px system-ui, -apple-system, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const safeTitle = String(title || 'Punto de interés');
  const words = safeTitle.split(/\s+/);
  const lines = [''];
  for (let index = 0; index < words.length; index += 1) {
    const lineIndex = lines.length - 1;
    const candidate = lines[lineIndex]
      ? `${lines[lineIndex]} ${words[index]}`
      : words[index];
    if (context.measureText(candidate).width <= 680 || lines.length === 2) {
      lines[lineIndex] = candidate;
    } else {
      lines.push(words[index]);
    }
  }

  const firstY = lines.length === 1 ? canvas.height * 0.51 : canvas.height * 0.38;
  for (let index = 0; index < lines.length; index += 1) {
    context.fillText(lines[index], canvas.width / 2, firstY + index * 62);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

export class Hotspot extends THREE.Group {
  constructor(data, { baseScale = 1, hoverScale = 1.16 } = {}) {
    super();

    if (!data?.id || !Array.isArray(data.position)) {
      throw new Error('Cada hotspot necesita un id y una posición [x, y, z].');
    }

    this.name = `hotspot:${data.id}`;
    this.data = data;
    this.hotspotId = data.id;
    this.isHovered = false;
    this.baseScale = baseScale;
    this.hoverScale = hoverScale;

    this.position.fromArray(data.position);
    this.scale.setScalar(baseScale);

    this._cameraWorldQuaternion = new THREE.Quaternion();
    this._parentWorldQuaternion = new THREE.Quaternion();

    this.marker = new THREE.Mesh(MARKER_GEOMETRY, MARKER_MATERIAL);
    this.marker.name = `${this.name}:marker`;
    this.marker.renderOrder = 4;

    this.ring = new THREE.Mesh(MARKER_RING_GEOMETRY, RING_MATERIAL);
    this.ring.name = `${this.name}:ring`;
    this.ring.position.z = -0.003;
    this.ring.renderOrder = 3;

    this.hitTarget = new THREE.Mesh(HIT_GEOMETRY, HIT_MATERIAL);
    this.hitTarget.name = `${this.name}:hit-target`;
    this.hitTarget.position.z = 0.018;
    this.hitTarget.renderOrder = 5;

    this.labelTexture = createLabelTexture(data.title);
    this.labelMaterial = new THREE.MeshBasicMaterial({
      map: this.labelTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
    });
    this.label = new THREE.Mesh(LABEL_GEOMETRY, this.labelMaterial);
    this.label.name = `${this.name}:label`;
    this.label.position.set(0, 0.35, -0.002);
    this.label.renderOrder = 4;

    this._tagInteractive(this.marker);
    this._tagInteractive(this.ring);
    this._tagInteractive(this.hitTarget);

    this.add(this.ring, this.marker, this.hitTarget, this.label);
  }

  _tagInteractive(object) {
    object.userData.interactive = true;
    object.userData.interactionType = 'hotspot';
    object.userData.hotspot = this;
    object.userData.hotspotId = this.hotspotId;
  }

  setHovered(hovered) {
    if (this.isHovered === hovered) {
      return;
    }

    this.isHovered = hovered;
    const scale = hovered ? this.hoverScale : this.baseScale;
    this.scale.setScalar(scale);
    this.marker.material = hovered ? MARKER_HOVER_MATERIAL : MARKER_MATERIAL;
    this.ring.material = hovered ? RING_HOVER_MATERIAL : RING_MATERIAL;
  }

  updateFacing(camera) {
    if (!camera) {
      return;
    }

    camera.getWorldQuaternion(this._cameraWorldQuaternion);

    if (this.parent) {
      this.parent.getWorldQuaternion(this._parentWorldQuaternion).invert();
      this.quaternion
        .copy(this._parentWorldQuaternion)
        .multiply(this._cameraWorldQuaternion);
    } else {
      this.quaternion.copy(this._cameraWorldQuaternion);
    }
  }

  dispose() {
    this.labelMaterial.dispose();
    this.labelTexture.dispose();
    this.removeFromParent();
  }
}

export function disposeSharedHotspotResources() {
  MARKER_GEOMETRY.dispose();
  MARKER_RING_GEOMETRY.dispose();
  HIT_GEOMETRY.dispose();
  LABEL_GEOMETRY.dispose();
  MARKER_MATERIAL.dispose();
  MARKER_HOVER_MATERIAL.dispose();
  RING_MATERIAL.dispose();
  RING_HOVER_MATERIAL.dispose();
  HIT_MATERIAL.dispose();
}

export default Hotspot;
